import useLocalStorageState, { announceLocalStorageChange } from "@/src/hooks/useLocalStorageState";

// Device-wide preferences: one localStorage key + default each. The settings
// sheet and the feature that reads a preference share the entry, so they can
// never drift on the key or the default.
const PREFERENCES = {
  editorFont: ["editor:font", ""],
  editorTextSize: ["editor:textSize", 18],
  editorLineHeight: ["editor:lineHeight", 1.75],
  chapterOrder: ["chapters:order", "asc"],
  shelfView: ["shelf:view", "series"],
  continueWriting: ["continueWriting:settings", { length: "medium", model: "auto" }],
  inlineAiContext: ["inlineAi:context", { placement: "around", size: "medium" }],
  defaultModel: ["model:default", "auto"],
  enterSends: ["chat:enterSends", true],
  // `words`: how much new text (inserted + deleted, ~6 chars a word) before the
  // next pause fires the action.
  autoReview: ["autoReview", { enabled: false, words: 500 }],
  autoVersioning: ["autoVersioning", { enabled: true, words: 250 }],
  readAloud: ["readAloud", { voice: "", speed: 1.0, language: "en-US" }],
};

export function usePreference(name) {
  const [key, defaultValue] = PREFERENCES[name];
  return useLocalStorageState(key, defaultValue);
}

// The chapters as a list shows them: the store stays in chapter order, and a
// writer who wants the newest chapter on top reads it reversed. Numbers come
// from the chapter, never from its place in the shown list.
export function useChapterList(chapters) {
  const [order] = usePreference("chapterOrder");
  const reversed = order === "desc";
  return {
    shown: reversed ? [...chapters].reverse() : chapters,
    // a shown index back to the store's index, for drag and drop
    toStoreIndex: (i) => (reversed ? chapters.length - 1 - i : i),
  };
}

export const chapterLabel = (chapter) => `CH ${String(chapter.chapterNumber).padStart(2, "0")}`;

// ---- Account sync ----------------------------------------------------------
// The server keeps one map per user (user_settings) under these same keys, so
// a preference follows the writer between devices. Local storage stays the
// cache: the page paints from it, the server's copy lands at login, and each
// local write is pushed. The margin is stored by hand as a bare string, so it
// gets its own read/write. The theme is deliberately per device (a dark laptop
// and a light desk are both right): never pushed, never hydrated.

const MARGIN_KEY = "manuscriptMargin";
const DEFAULTS = Object.fromEntries(Object.values(PREFERENCES));

export const SYNCED_KEYS = new Set([MARGIN_KEY, ...Object.keys(DEFAULTS)]);

// The local value of one key, in the server's terms; undefined when unset.
export function serverValueFor(key) {
  const raw = localStorage.getItem(key);
  if (raw === null) return undefined;
  return key === MARGIN_KEY ? raw : JSON.parse(raw);
}

let hydrating = false;
export const isHydratingPreferences = () => hydrating;

// The server's map into local storage, announced so every open control follows.
export function hydratePreferences(settings) {
  if (!settings || typeof settings !== "object") return;
  hydrating = true;
  try {
    for (const [key, value] of Object.entries(settings)) {
      if (!SYNCED_KEYS.has(key) || value == null) continue; // the server validates shapes
      localStorage.setItem(key, key === MARGIN_KEY ? value : JSON.stringify(value));
      announceLocalStorageChange(key);
    }
  } finally {
    hydrating = false;
  }
}

// The editor's font menu. `value` is what the toolbar's per-selection mark
// stores (kept stable: it lives in saved chapters); `css` is the stack the
// manuscript default renders with, so a face the machine lacks (Calibri on a
// Mac) falls to its metric twin instead of the browser's serif. "" is the
// manuscript's own face, Spectral.
export const EDITOR_FONTS = [
  { name: "Spectral", value: "", css: '"Spectral", Georgia, serif' },
  { name: "Montserrat", value: "var(--font-montserrat)", css: "var(--font-montserrat), sans-serif" },
  { name: "Inter", value: "Inter", css: "Inter, sans-serif" },
  { name: "Arial", value: "Arial", css: "Arial, Helvetica, sans-serif" },
  { name: "Calibri", value: "Calibri", css: "Calibri, Carlito, sans-serif" },
  { name: "Carlito", value: "Carlito", css: "Carlito, Calibri, sans-serif" },
  { name: "Georgia", value: "Georgia", css: 'Georgia, "Times New Roman", serif' },
  { name: "Monospace", value: "monospace", css: "ui-monospace, monospace" },
];

export const editorFontFor = (value) => EDITOR_FONTS.find((f) => f.value === value) ?? EDITOR_FONTS[0];
